/**
 * notificationModel.js - the notification bell's pure rules (10/11/26): what
 * each kind looks like, the filter chips, "5m ago", a release's date, and
 * where a click goes - a For you item through the Activity log's jump rules
 * (views/activity/activityActions.js), so it opens exactly what that log
 * would open; a What's new item to its tab. Server side:
 * server/lib/notifications.js and server/lib/releases.js.
 */
import { jumpOf, resolveJump } from '../../views/activity/activityActions';

export const NOTIFY_URL = '/api/notifications';

/** For you kinds: icon, colour and the word on the item. */
export const KIND_META = {
  assigned: { icon: 'check', color: '#1B5E20', bg: '#E8F5E9', label: 'Task' },
  mention: { icon: 'user', color: '#1565C0', bg: '#E3F2FD', label: 'Mention' },
  view_shared: { icon: 'filter', color: '#6A1B9A', bg: '#F3E5F5', label: 'Shared view' },
  po_won: { icon: 'dollar', color: '#2E7D32', bg: '#E8F5E9', label: 'PO / Won' },
  overdue: { icon: 'clock', color: '#B71C1C', bg: '#FFEBEE', label: 'Overdue' },
};

export const FOR_YOU_CHIPS = [
  { key: 'all', label: 'All', kinds: null },
  { key: 'tasks', label: 'Tasks', kinds: ['assigned', 'overdue'] },
  { key: 'mentions', label: 'Mentions', kinds: ['mention'] },
  { key: 'views', label: 'Shared views', kinds: ['view_shared'] },
  { key: 'wins', label: "PO's & wins", kinds: ['po_won'] },
];

/** "just now", "5m ago", "3h ago", "2d ago", then the date. */
export function timeAgo(iso, now = Date.now()) {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: d > 300 ? 'numeric' : undefined });
}

/** 0 -> '', 7 -> '7', 100 -> '99+'. */
export const badgeText = (n) => (!n ? '' : n > 99 ? '99+' : String(n));

/** A release's day: '2026-10-11' -> 'Oct 11, 2026' (no time zone shift). */
export function releaseDay(day) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
  if (!m) return '';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Where a What's new item goes: { href, tab } | { text } | null (nowhere: plain text). */
export function releaseDestination(item, canSee) {
  if (!item || !item.tab) return null;
  if (canSee && !canSee(item.tab)) return { text: 'Your access does not include that page.' };
  return { href: `#/${item.tab}`, tab: item.tab };
}

const VIEW_TABS = {
  projects: ['#/projects', 'projects'],
  opportunities: ['#/opportunities', 'opportunities'],
  'ksa-projects': ['#/ksa-overview', 'ksa-overview'],
  'ksa-opportunities': ['#/ksa-overview', 'ksa-overview'],
};

/**
 * Where a click on an item goes, checked against what the app holds (ctx is
 * the Activity log's jump context): { href, tab, verify? } | { text }.
 * `item` is a For you item ({ refKind, refPath, meta }) or an Activity log
 * row ({ method, path, detail }).
 */
export function destinationOf(item, ctx = {}) {
  if (!item) return { text: 'There is nothing to open.' };
  if (item.refKind === 'view') {
    const [href, tab] = VIEW_TABS[item.meta && item.meta.table] || [];
    if (!href) return { text: 'There is nothing to open.' };
    if (ctx.canSee && !ctx.canSee(tab)) return { text: 'Your access does not include the page this happened on.' };
    return { href, tab };
  }
  const row = item.refPath !== undefined ? { method: 'PUT', path: item.refPath || '', detail: { snapshot: item.meta || {} } } : item;
  if (!row.path) return { text: 'There is nothing to open.' };
  return resolveJump(jumpOf(row), ctx);
}
